import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import {
  ANTECEDENT_INTERVENTION_OPTIONS,
  CONSEQUENCE_INTERVENTION_OPTIONS,
  ESCALATION_DONTS,
  ESCALATION_DOS,
  FUNCTION_BY_VALUE,
  FUNCTIONS_OF_BEHAVIOR,
  MTSS_TIERS,
  TEACHING_STRATEGY_OPTIONS,
  TEAM_ROLES,
} from '../../../lib/pbisConstants';
import { useBehaviorPlans } from 'context/BehaviorPlansContext';
import IncidentDataSnapshot from './IncidentDataSnapshot';

const emptyPlan = (studentId) => ({
  studentId,
  mtssTier: 2,
  targetBehaviorDefinition: '',
  fbaSummary: '',
  hypothesizedFunction: '',
  hypothesisStatement: '',
  replacementBehavior: '',
  antecedentInterventions: [],
  consequenceInterventions: [],
  teachingStrategies: [],
  smartGoal: '',
  goalBaseline: '',
  goalTarget: '',
  goalMasteryCriteria: '',
  goalTargetDate: '',
  progressMonitoringPlan: '',
  escalationPlan: '',
  teamRoles: [],
  fidelityPlan: '',
  status: 'active',
  effectiveFrom: '',
  nextReviewDate: '',
});

const functionOptions = FUNCTIONS_OF_BEHAVIOR.map((f) => ({
  value: f.value,
  label: f.label,
  description: f.description,
}));

const tierOptions = MTSS_TIERS.map((t) => ({
  value: t.value,
  label: t.label,
  description: t.description,
}));

const BehaviorSupportPlan = ({ studentId, studentName }) => {
  const { activePlanForStudent, savePlan, isLive } = useBehaviorPlans();
  const existing = studentId ? activePlanForStudent(studentId) : null;

  const [isEditing, setIsEditing] = useState(!existing);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [form, setForm] = useState(() => existing || emptyPlan(studentId));

  useEffect(() => {
    setForm(existing || emptyPlan(studentId));
    setIsEditing(!existing);
    setSaveError(null);
  }, [existing?.id, studentId]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const toggleInList = (field, value) => {
    setForm((prev) => {
      const list = Array.isArray(prev[field]) ? prev[field] : [];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...prev, [field]: next };
    });
  };

  const addTeamRow = () =>
    setForm((prev) => ({
      ...prev,
      teamRoles: [
        ...(Array.isArray(prev.teamRoles) ? prev.teamRoles : []),
        { name: '', role: '', responsibility: '' },
      ],
    }));

  const updateTeamRow = (index, field, value) =>
    setForm((prev) => {
      const next = [...(Array.isArray(prev.teamRoles) ? prev.teamRoles : [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, teamRoles: next };
    });

  const removeTeamRow = (index) =>
    setForm((prev) => {
      const next = [...(Array.isArray(prev.teamRoles) ? prev.teamRoles : [])];
      next.splice(index, 1);
      return { ...prev, teamRoles: next };
    });

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      await savePlan({ ...form, studentId });
      setIsEditing(false);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save behavior support plan');
    } finally {
      setSaving(false);
    }
  };

  const tierMeta = MTSS_TIERS.find((t) => t.value === form.mtssTier);
  const functionMeta = form.hypothesizedFunction ? FUNCTION_BY_VALUE[form.hypothesizedFunction] : null;

  const smartGoalPreview = useMemo(() => {
    const { specific, measurable, mastery } = {
      specific: form.smartGoal?.trim(),
      measurable: form.goalTarget?.trim(),
      mastery: form.goalMasteryCriteria?.trim(),
    };
    if (!specific && !measurable && !mastery) return '';
    const parts = [];
    if (specific) parts.push(specific);
    if (measurable) parts.push(`Target: ${measurable}`);
    if (form.goalBaseline?.trim()) parts.push(`Baseline: ${form.goalBaseline.trim()}`);
    if (mastery) parts.push(`Mastery: ${mastery}`);
    if (form.goalTargetDate) parts.push(`By ${form.goalTargetDate}`);
    return parts.join(' · ');
  }, [form.smartGoal, form.goalTarget, form.goalBaseline, form.goalMasteryCriteria, form.goalTargetDate]);

  if (!isEditing && existing) {
    return (
      <PlanReadView
        plan={existing}
        onEdit={() => setIsEditing(true)}
        isLive={isLive}
        studentId={studentId}
        studentName={studentName}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Incident data snapshot — teachers were writing this plan from
          scratch with no view into the ABC data they'd already logged,
          which is exactly the workflow gap the feedback called out.
          The snapshot rolls up incidents for this student, surfaces the
          patterns the FBA hypothesis needs, and offers per-field
          "Apply to plan" buttons that write into the form state below. */}
      <IncidentDataSnapshot
        studentId={studentId}
        studentName={studentName}
        plan={form}
        onApplySuggestion={update}
        mode="edit"
      />

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-card-foreground flex items-center gap-2">
              <Icon name="ClipboardList" size={20} className="text-primary" />
              Behavior Support Plan
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Individualized BIP / BSP for {studentName || 'this student'}. Fields cover the standard FBA/BIP components used in PBIS Tier 2/3 planning.
            </p>
          </div>
          <div className="text-xs text-muted-foreground">
            {isLive ? 'Saved to Supabase' : 'Saved locally (demo mode)'}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="MTSS tier"
            options={tierOptions}
            value={form.mtssTier}
            onChange={(v) => update('mtssTier', v)}
            required
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              label="Effective from"
              type="date"
              value={form.effectiveFrom || ''}
              onChange={(e) => update('effectiveFrom', e?.target?.value)}
            />
            <Input
              label="Next review"
              type="date"
              value={form.nextReviewDate || ''}
              onChange={(e) => update('nextReviewDate', e?.target?.value)}
            />
          </div>
        </div>
      </div>

      {/* Target behavior definition ------------------------------------- */}
      <Section title="Target behavior (operational definition)" icon="Target" required>
        <textarea
          value={form.targetBehaviorDefinition}
          onChange={(e) => update('targetBehaviorDefinition', e?.target?.value)}
          placeholder='Observable + measurable. Example: "Student refuses to start assigned work by putting head down, pushing paper away, or saying no for more than 60 seconds after directions are given."'
          rows={3}
          required
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        <Hint>Anyone reading the plan should be able to identify an instance without asking follow-up questions.</Hint>
      </Section>

      {/* FBA + function -------------------------------------------------- */}
      <Section title="Function of behavior (FBA summary)" icon="Brain">
        <div className="space-y-4">
          <textarea
            value={form.fbaSummary}
            onChange={(e) => update('fbaSummary', e?.target?.value)}
            placeholder="Summary of ABC data patterns, interviews, direct observation. What settings, times, and antecedents show up most?"
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
          <Select
            label="Hypothesized function"
            options={functionOptions}
            value={form.hypothesizedFunction}
            onChange={(v) => update('hypothesizedFunction', v)}
          />
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Hypothesis statement</label>
            <textarea
              value={form.hypothesisStatement}
              onChange={(e) => update('hypothesisStatement', e?.target?.value)}
              placeholder="When [antecedent], the student engages in [target behavior] in order to obtain / avoid [function]."
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
            <Hint>The "When … the student … in order to …" frame is the standard FBA hypothesis structure used across special-education audit rubrics.</Hint>
          </div>
        </div>
      </Section>

      {/* Replacement behavior ------------------------------------------- */}
      <Section title="Functionally-equivalent replacement behavior" icon="Repeat">
        <textarea
          value={form.replacementBehavior}
          onChange={(e) => update('replacementBehavior', e?.target?.value)}
          placeholder='What the team teaches instead. Must serve the same function. Example (escape): "Student will hand a break card to the teacher and take a 3-minute cool-down instead of throwing materials."'
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        <Hint>The replacement behavior must be easier and more efficient than the target behavior — otherwise students won't use it.</Hint>
      </Section>

      {/* Antecedent interventions --------------------------------------- */}
      <Section title="Antecedent (proactive) interventions" icon="Sunrise">
        <ChipList
          options={ANTECEDENT_INTERVENTION_OPTIONS}
          selected={form.antecedentInterventions}
          onToggle={(v) => toggleInList('antecedentInterventions', v)}
        />
      </Section>

      {/* Teaching strategies ------------------------------------------- */}
      <Section title="Teaching strategies (for the replacement behavior)" icon="GraduationCap">
        <ChipList
          options={TEACHING_STRATEGY_OPTIONS}
          selected={form.teachingStrategies}
          onToggle={(v) => toggleInList('teachingStrategies', v)}
        />
      </Section>

      {/* Consequence strategies ---------------------------------------- */}
      <Section title="Consequence (reactive) strategies" icon="ArrowDown">
        <ChipList
          options={CONSEQUENCE_INTERVENTION_OPTIONS}
          selected={form.consequenceInterventions}
          onToggle={(v) => toggleInList('consequenceInterventions', v)}
        />
      </Section>

      {/* SMART goal ---------------------------------------------------- */}
      <Section title="SMART behavior goal" icon="Target">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              Specific — what specifically will change?
            </label>
            <textarea
              value={form.smartGoal}
              onChange={(e) => update('smartGoal', e?.target?.value)}
              rows={2}
              placeholder='Example: "Student will request breaks using a break card during independent work…"'
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
          <Input
            label="Baseline"
            value={form.goalBaseline || ''}
            onChange={(e) => update('goalBaseline', e?.target?.value)}
            placeholder='e.g. "0 requests / week"'
          />
          <Input
            label="Measurable target"
            value={form.goalTarget || ''}
            onChange={(e) => update('goalTarget', e?.target?.value)}
            placeholder='e.g. "at least 3 requests / day"'
          />
          <Input
            label="Mastery criteria"
            value={form.goalMasteryCriteria || ''}
            onChange={(e) => update('goalMasteryCriteria', e?.target?.value)}
            placeholder='e.g. "for 3 consecutive weeks"'
          />
          <Input
            label="Target date"
            type="date"
            value={form.goalTargetDate || ''}
            onChange={(e) => update('goalTargetDate', e?.target?.value)}
          />
        </div>
        {smartGoalPreview && (
          <div className="mt-3 p-3 rounded-md bg-primary/5 border border-primary/20 text-sm">
            <span className="font-medium">SMART preview: </span>
            {smartGoalPreview}
          </div>
        )}
      </Section>

      {/* Progress monitoring ------------------------------------------- */}
      <Section title="Progress monitoring plan" icon="LineChart">
        <textarea
          value={form.progressMonitoringPlan}
          onChange={(e) => update('progressMonitoringPlan', e?.target?.value)}
          placeholder="How often will data be collected, by whom, using what method (frequency count, event recording, duration, ABC log, rating scale)? When will the team review it?"
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </Section>

      {/* Fidelity ------------------------------------------------------ */}
      <Section title="Fidelity of implementation" icon="ShieldCheck">
        <textarea
          value={form.fidelityPlan}
          onChange={(e) => update('fidelityPlan', e?.target?.value)}
          placeholder="How will the team verify the plan is implemented as written? (Checklists, walk-throughs, coaching cycles, self-report rating.)"
          rows={2}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </Section>

      {/* Escalation ---------------------------------------------------- */}
      <Section title="Escalation / crisis plan" icon="AlertTriangle">
        <textarea
          value={form.escalationPlan}
          onChange={(e) => update('escalationPlan', e?.target?.value)}
          placeholder="If the student escalates, what specifically will staff do (and not do) to keep everyone safe and de-escalate? Include when to call for additional support."
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div className="p-3 rounded-md bg-success/10 border border-success/30">
            <p className="text-xs font-semibold text-success mb-2 flex items-center gap-1">
              <Icon name="ThumbsUp" size={14} /> Do
            </p>
            <ul className="text-xs space-y-1">
              {ESCALATION_DOS.map((d) => (
                <li key={d}>· {d}</li>
              ))}
            </ul>
          </div>
          <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30">
            <p className="text-xs font-semibold text-destructive mb-2 flex items-center gap-1">
              <Icon name="ThumbsDown" size={14} /> Don't
            </p>
            <ul className="text-xs space-y-1">
              {ESCALATION_DONTS.map((d) => (
                <li key={d}>· {d}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Team roles ---------------------------------------------------- */}
      <Section title="Team & roles" icon="Users">
        <div className="space-y-3">
          {(form.teamRoles || []).map((row, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
              <div className="md:col-span-4">
                <Input
                  label={index === 0 ? 'Name' : ''}
                  value={row.name || ''}
                  onChange={(e) => updateTeamRow(index, 'name', e?.target?.value)}
                  placeholder="Person's name"
                />
              </div>
              <div className="md:col-span-4">
                <Select
                  label={index === 0 ? 'Role' : ''}
                  options={TEAM_ROLES.map((r) => ({ value: r.value, label: r.label }))}
                  value={row.role || ''}
                  onChange={(v) => {
                    updateTeamRow(index, 'role', v);
                    // Autofill responsibility from the canonical list, but
                    // only if the user hasn't typed their own.
                    if (!row.responsibility) {
                      const canonical = TEAM_ROLES.find((r) => r.value === v)?.responsibility;
                      if (canonical) updateTeamRow(index, 'responsibility', canonical);
                    }
                  }}
                />
              </div>
              <div className="md:col-span-3">
                <Input
                  label={index === 0 ? 'Responsibility' : ''}
                  value={row.responsibility || ''}
                  onChange={(e) => updateTeamRow(index, 'responsibility', e?.target?.value)}
                  placeholder="Their responsibility"
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => removeTeamRow(index)}
                  aria-label="Remove team member"
                >
                  <Icon name="Trash2" size={16} className="text-destructive" />
                </Button>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addTeamRow}>
            <Icon name="UserPlus" size={14} />
            Add team member
          </Button>
        </div>
      </Section>

      {saveError && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-destructive">
          {saveError}
        </div>
      )}

      <div className="flex justify-end gap-3">
        {existing && (
          <Button type="button" variant="outline" onClick={() => setIsEditing(false)} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={saving}>
          <Icon name="Save" size={16} />
          Save Plan
        </Button>
      </div>
    </form>
  );
};

const Section = ({ title, icon, required, children }) => (
  <section className="bg-card border border-border rounded-lg p-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon name={icon} size={18} className="text-primary" />
      <h3 className="font-semibold text-card-foreground">{title}</h3>
      {required && <span className="text-destructive">*</span>}
    </div>
    {children}
  </section>
);

const Hint = ({ children }) => <p className="mt-2 text-xs text-muted-foreground">{children}</p>;

const ChipList = ({ options, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const isSelected = selected?.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onToggle(opt)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            isSelected
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-foreground border-border hover:border-primary'
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const PlanReadView = ({ plan, onEdit, isLive, studentId, studentName }) => {
  // Derive badge metadata directly from the plan so read view stays in sync
  // even if the edit-mode form state is stale (e.g. right after an async
  // refresh from Supabase). Keeps display consistent with the persisted row.
  const tierMeta = MTSS_TIERS.find((t) => t.value === plan?.mtssTier);
  const functionMeta = plan?.hypothesizedFunction
    ? FUNCTION_BY_VALUE[plan.hypothesizedFunction]
    : null;

  return (
  <div className="space-y-6">
    {/* Snapshot in read mode too — a reviewer opening this plan should
        see the underlying incident data the plan was built from, side
        by side with the plan itself. The read variant hides the
        "Apply to plan" affordances (mode="read"). */}
    <IncidentDataSnapshot
      studentId={studentId}
      studentName={studentName}
      plan={plan}
      mode="read"
    />

    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground flex items-center gap-2">
            <Icon name="ClipboardList" size={20} className="text-primary" />
            Behavior Support Plan
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isLive ? 'Synced with Supabase' : 'Local demo copy'} · Status: {plan.status}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Icon name="Edit" size={14} />
          Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tierMeta && (
          <Badge
            title="MTSS Tier"
            value={tierMeta.label}
            hint={tierMeta.description}
            color={tierMeta.color}
          />
        )}
        {functionMeta && (
          <Badge
            title="Hypothesized function"
            value={functionMeta.label}
            hint={functionMeta.description}
            color={functionMeta.color}
          />
        )}
        <Badge
          title="Next review"
          value={plan.nextReviewDate ? new Date(plan.nextReviewDate).toLocaleDateString() : '—'}
          hint={plan.effectiveFrom ? `In effect since ${new Date(plan.effectiveFrom).toLocaleDateString()}` : ''}
          color="#6b7280"
        />
      </div>
    </div>

    <ReadSection title="Target behavior (operational)" icon="Target" body={plan.targetBehaviorDefinition} />
    <ReadSection title="FBA summary" icon="Brain" body={plan.fbaSummary} />
    <ReadSection title="Hypothesis statement" icon="Zap" body={plan.hypothesisStatement} />
    <ReadSection title="Replacement behavior" icon="Repeat" body={plan.replacementBehavior} />

    <ReadListSection title="Antecedent (proactive) interventions" icon="Sunrise" items={plan.antecedentInterventions} />
    <ReadListSection title="Teaching strategies" icon="GraduationCap" items={plan.teachingStrategies} />
    <ReadListSection title="Consequence (reactive) strategies" icon="ArrowDown" items={plan.consequenceInterventions} />

    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon name="Target" size={18} className="text-primary" />
        <h3 className="font-semibold text-card-foreground">SMART Goal</h3>
      </div>
      <p className="text-sm text-card-foreground">{plan.smartGoal || '—'}</p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">Baseline</p>
          <p>{plan.goalBaseline || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Target</p>
          <p>{plan.goalTarget || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Mastery</p>
          <p>{plan.goalMasteryCriteria || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Target date</p>
          <p>{plan.goalTargetDate ? new Date(plan.goalTargetDate).toLocaleDateString() : '—'}</p>
        </div>
      </div>
    </div>

    <ReadSection title="Progress monitoring" icon="LineChart" body={plan.progressMonitoringPlan} />
    <ReadSection title="Fidelity of implementation" icon="ShieldCheck" body={plan.fidelityPlan} />
    <ReadSection title="Escalation plan" icon="AlertTriangle" body={plan.escalationPlan} />

    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon name="Users" size={18} className="text-primary" />
        <h3 className="font-semibold text-card-foreground">Team</h3>
      </div>
      {Array.isArray(plan.teamRoles) && plan.teamRoles.length > 0 ? (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Role</th>
                <th className="text-left px-3 py-2 font-medium">Responsibility</th>
              </tr>
            </thead>
            <tbody>
              {plan.teamRoles.map((row, i) => {
                const safeRow = row && typeof row === 'object' ? row : {};
                const roleLabel =
                  TEAM_ROLES.find((r) => r.value === safeRow.role)?.label || safeRow.role;
                return (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2">{safeRow.name || '—'}</td>
                    <td className="px-3 py-2">{roleLabel || '—'}</td>
                    <td className="px-3 py-2">{safeRow.responsibility || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No team members recorded yet.</p>
      )}
    </div>
  </div>
  );
};

const Badge = ({ title, value, hint, color }) => (
  <div className="p-4 rounded-lg border" style={{ borderColor: `${color}55`, backgroundColor: `${color}0d` }}>
    <p className="text-xs uppercase tracking-wide font-medium" style={{ color }}>
      {title}
    </p>
    <p className="text-lg font-semibold text-foreground mt-1">{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const ReadSection = ({ title, icon, body }) => (
  <div className="bg-card border border-border rounded-lg p-6">
    <div className="flex items-center gap-2 mb-3">
      <Icon name={icon} size={18} className="text-primary" />
      <h3 className="font-semibold text-card-foreground">{title}</h3>
    </div>
    <p className="text-sm whitespace-pre-wrap text-card-foreground">{body || '—'}</p>
  </div>
);

const ReadListSection = ({ title, icon, items }) => (
  <div className="bg-card border border-border rounded-lg p-6">
    <div className="flex items-center gap-2 mb-3">
      <Icon name={icon} size={18} className="text-primary" />
      <h3 className="font-semibold text-card-foreground">{title}</h3>
    </div>
    {Array.isArray(items) && items.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {items.map((item, idx) => (
          <span
            key={`${item}-${idx}`}
            className="px-3 py-1 rounded-full text-xs bg-muted text-muted-foreground border border-border"
          >
            {typeof item === 'string' ? item : String(item ?? '')}
          </span>
        ))}
      </div>
    ) : (
      <p className="text-sm text-muted-foreground">—</p>
    )}
  </div>
);

export default BehaviorSupportPlan;
